/**
 * LicenseBanner — sistema notice/banner v1.0.38.
 *
 * Renderiza arriba del Layout cuando la licencia tiene avisos no-bloqueantes.
 * NO restringe funcionalidad — solo informa.
 *
 * Mapeo mode → render:
 *   active   → no muestra nada
 *   warning  → banner amarillo con notices array
 *   grace    → banner azul "reconectando…"
 *   stale    → banner amarillo "sin contacto desde Xh" (dismissible hasta logout)
 *   blocked  → no aplica acá (Layout muestra SuspensionScreen)
 *   off / no_license → no aplica acá (Layout muestra LicenseEntryScreen o nada)
 *
 * Los notices vienen de ByteControl (creados por el admin en /admin/licenses/{id}/notices)
 * + auto-warning de límite de plan (≥90% del max_clients) cacheados localmente
 * en licenses.notices_cache. Se refrescan en cada heartbeat OK.
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Info, AlertCircle, X } from "lucide-react";
import { apiCall } from "@/lib/api";

type LicenseNotice = {
  id?: string | null;
  kind: string;
  severity: "info" | "warning" | "danger";
  title: string;
  body: string;
  expires_at?: string | null;
  dismissible: boolean;
  created_at?: string | null;
};

type LicenseStatus = {
  enabled: boolean;
  mode: string;
  message: string;
  notices: LicenseNotice[];
  hours_without_contact: number | null;
  stale_block_hours: number | null;
};

const DISMISS_KEY_PREFIX = "byteisp_license_banner_dismissed_";

/** Devuelve el color y el icon según severity. */
function severityStyles(severity: "info" | "warning" | "danger") {
  switch (severity) {
    case "danger":
      return {
        wrap: "border-red-500/40 bg-red-500/10 dark:border-red-500/30 dark:bg-red-500/10",
        title: "text-red-900 dark:text-red-200",
        body: "text-red-800 dark:text-red-300/90",
        Icon: AlertCircle,
        iconCls: "text-red-600 dark:text-red-400",
      };
    case "warning":
      return {
        wrap: "border-yellow-500/40 bg-yellow-500/10 dark:border-yellow-500/30 dark:bg-yellow-500/10",
        title: "text-yellow-900 dark:text-yellow-200",
        body: "text-yellow-800 dark:text-yellow-300/90",
        Icon: AlertTriangle,
        iconCls: "text-yellow-600 dark:text-yellow-400",
      };
    default:
      return {
        wrap: "border-blue-500/40 bg-blue-500/10 dark:border-blue-500/30 dark:bg-blue-500/10",
        title: "text-blue-900 dark:text-blue-200",
        body: "text-blue-800 dark:text-blue-300/90",
        Icon: Info,
        iconCls: "text-blue-600 dark:text-blue-400",
      };
  }
}

function NoticeRow({
  notice,
  onDismiss,
}: {
  notice: LicenseNotice;
  onDismiss: ((n: LicenseNotice) => void) | null;
}) {
  const s = severityStyles(notice.severity);
  return (
    <div className={`mb-2 flex items-start gap-3 rounded-md border p-3 ${s.wrap}`}>
      <s.Icon className={`mt-0.5 h-4 w-4 shrink-0 ${s.iconCls}`} />
      <div className="flex-1">
        <div className={`text-xs font-semibold ${s.title}`}>{notice.title}</div>
        <div className={`mt-1 text-xs ${s.body}`}>{notice.body}</div>
      </div>
      {notice.dismissible && onDismiss && (
        <button
          onClick={() => onDismiss(notice)}
          className="text-yellow-700 hover:text-yellow-900 dark:text-yellow-400 dark:hover:text-yellow-200"
          title="Ocultar hasta cerrar sesión"
        >
          <X size={14} />
        </button>
      )}
    </div>
  );
}

/** Genera un notice virtual para mode=grace o stale (no viene de ByteControl). */
function autoConnectivityNotice(status: LicenseStatus): LicenseNotice | null {
  if (status.mode === "grace") {
    const h = status.hours_without_contact ?? 0;
    return {
      id: "auto:grace",
      kind: "connectivity",
      severity: "info",
      title: "Reconectando con el servidor de licencias…",
      body:
        h > 0
          ? `El último contacto exitoso fue hace ${h}h. El sistema sigue funcionando con normalidad.`
          : "El servidor de licencias no respondió en el último heartbeat. Reintentando.",
      dismissible: false, // grace es informativo, dejamos visible hasta que reconecte
      expires_at: null,
      created_at: null,
    };
  }
  if (status.mode === "stale") {
    const h = status.hours_without_contact ?? 0;
    const blockH = status.stale_block_hours ?? 168;
    return {
      id: "auto:stale",
      kind: "connectivity",
      severity: "warning",
      title: `Sin contacto con el servidor de licencias hace ${h}h`,
      body:
        `Verifica conectividad de tu instalación. Tras ${blockH}h de silencio el ` +
        "sistema se bloqueará automáticamente como medida de seguridad.",
      dismissible: true,
      expires_at: null,
      created_at: null,
    };
  }
  return null;
}

export function LicenseBanner() {
  // Por defecto re-render si cambia mode/notices
  const { data: status } = useQuery<LicenseStatus>({
    queryKey: ["licensing-status"],
    queryFn: () => apiCall<LicenseStatus>("get", "/api/v1/licensing/status"),
    staleTime: 30_000,
    refetchInterval: 60_000,
    retry: false,
  });

  // sessionStorage por notice id — re-aparecen al cerrar sesión
  const [dismissed, setDismissed] = useState<Record<string, boolean>>(() => {
    if (typeof window === "undefined") return {};
    const out: Record<string, boolean> = {};
    for (let i = 0; i < sessionStorage.length; i++) {
      const k = sessionStorage.key(i);
      if (k && k.startsWith(DISMISS_KEY_PREFIX)) {
        out[k.slice(DISMISS_KEY_PREFIX.length)] = true;
      }
    }
    return out;
  });

  if (!status || !status.enabled) return null;
  // Mostrar el banner si:
  //   1) mode es warning/grace/stale (estados con auto-banner sintético), O
  //   2) hay notices custom en cache (cualquier severity, incluso info, llegan
  //      desde ByteControl con mode=active todavía).
  // Modes 'active' sin notices y 'blocked'/'no_license' (Layout maneja screen
  // dedicada) no renderizan acá.
  const hasCustomNotices = (status.notices || []).length > 0;
  const isAutoMode = ["warning", "grace", "stale"].includes(status.mode);
  if (!hasCustomNotices && !isAutoMode) return null;

  const notices: LicenseNotice[] = [...(status.notices || [])];
  const auto = autoConnectivityNotice(status);
  if (auto) notices.push(auto);

  if (notices.length === 0) return null;

  const handleDismiss = (n: LicenseNotice) => {
    const id = n.id || `${n.kind}:${n.title}`;
    sessionStorage.setItem(DISMISS_KEY_PREFIX + id, "1");
    setDismissed((s) => ({ ...s, [id]: true }));
  };

  // Filtrar dismissed por id (o id sintético kind:title si no tiene)
  const visible = notices.filter((n) => {
    const id = n.id || `${n.kind}:${n.title}`;
    return !dismissed[id];
  });

  if (visible.length === 0) return null;

  return (
    <div>
      {visible.map((n, i) => (
        <NoticeRow
          key={n.id || `${n.kind}-${i}`}
          notice={n}
          onDismiss={n.dismissible ? handleDismiss : null}
        />
      ))}
    </div>
  );
}
